/**
 * Sponsor Discovery program hook - effect runner.
 * 
 * Elm-ish architecture: This is the ONLY place that combines:
 * - useState/useReducer for state management
 * - dispatch for message handling
 * - update() for pure state transitions
 * - runEffect() for side effects (outside the reducer)
 * 
 * The reducer is PURE - it only returns new state.
 * Effects are queued in state and executed via useEffect.
 * 
 * This hook bridges the pure Elm model to React's rendering.
 */
import { useReducer, useEffect, useCallback, useRef } from 'react';
import { initialModel, type SponsorDiscoveryModel } from './model';
import { type SponsorDiscoveryMsg } from './messages';
import { update, type SponsorDiscoveryEffect } from './update';
import { runEffect, type EffectContext } from './effects';

/**
 * Result of the program hook - exposes model and action callbacks.
 */
export interface UseSponsorDiscoveryProgramResult {
  /** Current model state */
  readonly model: SponsorDiscoveryModel;
  /** Start the discovery flow */
  readonly start: () => void;
  /** Skip the current card */
  readonly skip: () => void;
  /** Save/like the current card */
  readonly save: () => void;
  /** Request an intro for the current card */
  readonly requestIntro: () => void;
  /** Mark current action as succeeded and advance */
  readonly actionSucceeded: () => void;
  /** Mark current action as failed */
  readonly actionFailed: (error: string) => void;
  /** Retry after an error */
  readonly retry: () => void;
  /** Load more profiles */
  readonly loadMore: () => void;
}

/**
 * Program state - includes model and pending effects.
 * Effects are queued in state and executed outside the reducer.
 */
type ProgramState = {
  readonly model: SponsorDiscoveryModel;
  readonly pendingEffects: readonly SponsorDiscoveryEffect[];
};

/**
 * Initial program state.
 */
const initialState: ProgramState = {
  model: initialModel,
  pendingEffects: [],
};

/**
 * Create the effect context for running effects.
 * Uses the ref to get the current dispatch function.
 */
function createEffectContext(dispatch: (msg: SponsorDiscoveryMsg) => void): EffectContext {
  return { dispatch };
}

/**
 * The main program hook - orchestrates state and effects.
 * 
 * The reducer is PURE - effects are NOT run inside the reducer.
 * Instead, effects are queued and executed via useEffect.
 * 
 * Usage:
 * ```tsx
 * function DiscoverPage() {
 *   const { model, start, skip, save, requestIntro, retry, loadMore } = useSponsorDiscoveryProgram();
 *   
 *   useEffect(() => { start(); }, [start]);
 *   
 *   return <SponsorDiscoveryView model={model} onSkip={skip} onSave={save} onRequestIntro={requestIntro} onRetry={retry} />;
 * }
 * ```
 */
export function useSponsorDiscoveryProgram(): UseSponsorDiscoveryProgramResult {
  const dispatchRef = useRef<((msg: SponsorDiscoveryMsg) => void)>(() => {});
  
  const [state, baseDispatch] = useReducer(
    (currentState: ProgramState, msg: SponsorDiscoveryMsg): ProgramState => {
      // Pure reducer - only returns new state
      const result = update(currentState.model, msg);
      
      return {
        model: result.model,
        pendingEffects: result.effects,
      };
    },
    initialState
  );

  // Keep dispatch ref updated for effect execution
  dispatchRef.current = baseDispatch;

  // Execute pending effects outside the reducer via useEffect
  useEffect(() => {
    if (state.pendingEffects.length === 0) return;

    const context = createEffectContext(baseDispatch);
    
    for (const effect of state.pendingEffects) {
      void runEffect(effect, context);
    }
  }, [state.pendingEffects, baseDispatch]);

  // Create action callbacks that dispatch messages
  const start = useCallback(() => {
    baseDispatch({ type: 'Started' });
  }, []);

  const skip = useCallback(() => {
    baseDispatch({ type: 'SkipClicked' });
  }, []);

  const save = useCallback(() => {
    baseDispatch({ type: 'SaveClicked' });
  }, []);

  const requestIntro = useCallback(() => {
    baseDispatch({ type: 'RequestIntroClicked' });
  }, []);

  const actionSucceeded = useCallback(() => {
    baseDispatch({ type: 'ActionSucceeded' });
  }, []);

  const actionFailed = useCallback((error: string) => {
    baseDispatch({ type: 'ActionFailed', error });
  }, []);

  const retry = useCallback(() => {
    baseDispatch({ type: 'RetryClicked' });
  }, []);

  const loadMore = useCallback(() => {
    baseDispatch({ type: 'LoadMoreClicked' });
  }, []);

  return {
    model: state.model,
    start,
    skip,
    save,
    requestIntro,
    actionSucceeded,
    actionFailed,
    retry,
    loadMore,
  };
}
